"use client";

import { Check, Copy } from "lucide-react";
import { useState } from "react";

import { Button, type ButtonProps } from "@/components/ui/button";

/**
 * Copy `text` to the clipboard, returning whether it succeeded.
 *
 * The async Clipboard API (`navigator.clipboard`) only exists in secure
 * contexts (HTTPS or localhost); on plain HTTP it is `undefined`, which used to
 * make callers fail silently. We fall back to a hidden `<textarea>` +
 * `document.execCommand("copy")`, which works over HTTP and in older browsers.
 */
export async function copyToClipboard(text: string): Promise<boolean> {
  if (typeof navigator !== "undefined" && navigator.clipboard?.writeText) {
    try {
      await navigator.clipboard.writeText(text);
      return true;
    } catch {
      /* fall through to the execCommand fallback below */
    }
  }

  if (typeof document === "undefined") return false;
  const textarea = document.createElement("textarea");
  textarea.value = text;
  // Keep it out of view and unfocusable-looking, but still selectable.
  textarea.setAttribute("readonly", "");
  textarea.style.position = "fixed";
  textarea.style.top = "-9999px";
  textarea.style.opacity = "0";
  document.body.appendChild(textarea);
  try {
    textarea.focus();
    textarea.select();
    textarea.setSelectionRange(0, text.length);
    return document.execCommand("copy");
  } catch {
    return false;
  } finally {
    document.body.removeChild(textarea);
  }
}

/**
 * Copy-to-clipboard button. Shows a transient "Copied" state. Used for the
 * referral link, API keys, and anywhere a value should be one-click copyable.
 */
export function CopyButton({
  value,
  label = "Copy",
  copiedLabel = "Copied",
  ...props
}: {
  value: string;
  label?: string;
  copiedLabel?: string;
} & Omit<ButtonProps, "onClick" | "children">) {
  const [copied, setCopied] = useState(false);

  async function copy() {
    if (!value) return;
    const ok = await copyToClipboard(value);
    if (ok) {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  }

  return (
    <Button type="button" variant="outline" onClick={copy} {...props}>
      {copied ? (
        <>
          <Check aria-hidden="true" /> {copiedLabel}
        </>
      ) : (
        <>
          <Copy aria-hidden="true" /> {label}
        </>
      )}
    </Button>
  );
}
